import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import { NOT_UPSELL_LOCALIZATIONS } from 'nove8/constants/payment'
import { PaymentSystem } from 'nove8/constants/root-constants'
import { getSelectedAnswers } from 'nove8/helpers/getSelectedAnswers'
import { TStep } from 'nove8/types'
import { LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'
import { PageIdWoofzK9Bl } from './constants/pages'
import { CALM_OPTION } from './pages/questions/DuringWalkQuestion'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const dogHasNoPullingLeash = () => {
  const answers =
    useZustandSessionStore.getState().userAnswers ||
    useZustandGlobalStore.getState().lead?.quizzes ||
    null
  return answers?.leash_pulling_k9_bl === 'no'
}

const hasWalkProblems = () => {
  const answers =
    useZustandSessionStore.getState().userAnswers ||
    useZustandGlobalStore.getState().lead?.quizzes ||
    null

  return (
    answers?.during_walk_k9_bl !== 'none' &&
    answers?.during_walk_k9_bl?.[0] !== CALM_OPTION
  )
}

const hasDogActDiferentlyAtHome = () => {
  const answers =
    useZustandSessionStore.getState().userAnswers ||
    useZustandGlobalStore.getState().lead?.quizzes ||
    null
  return answers?.dog_act_k9_bl === 'no'
}

const dogHasNoAgressiveBehavior = () => {
  const answers =
    useZustandSessionStore.getState().userAnswers ||
    useZustandGlobalStore.getState().lead?.quizzes ||
    null
  return answers?.agressive_behavior_k9_bl === 'no'
}

const hasSelectedIgnoringCommands = () => {
  const answers =
    useZustandSessionStore.getState().userAnswers ||
    useZustandGlobalStore.getState().lead?.quizzes ||
    null

  const behaviorIssues = getSelectedAnswers(
    answers,
    PageId.BEHAVIOR_ISSUES_K9_BL,
  ) as string[]

  return behaviorIssues.includes('ignoring_commands')
}

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const isBreedIdSetupK9 = () => {
  const language = useZustandSessionStore.getState().language
  const config = useZustandGlobalStore.getState().firebaseRemoteConfig
  return config && language === LOCALES.EN
    ? config.getBoolean('breed_id_setup_k9')
    : false
}

export const getXStateConfigWoofzK9Bl = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const url = new URL(window.location.href)
          const pageId = url.pathname.split('/').pop()
          const isNewsletterPage = pageId === PageId.NEWSLETTER_URGE_K9_BL

          return !hasEmail && isNewsletterPage
        },
        target: pages.find((p) => p.pageId.includes('email'))?.pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !hasEmail &&
            !hasSubscription &&
            !hasAccount &&
            hasAnswers &&
            pageIdFromUrl !== PageIdWoofzK9Bl.SCRATCH_SCREEN_K9_BL
          )
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return pageIdFromUrl === PageIdWoofzK9Bl.SCRATCH_SCREEN_K9_BL
        },
        target: PageIdWoofzK9Bl.EMAIL_K9_BL,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageIdWoofzK9Bl.DOWNLOAD_K9_BL,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_K9_BL,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageIdWoofzK9Bl.SUBSCRIPTIONS_K9_BL,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.LEASH_PULLING_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: dogHasNoPullingLeash,
                target: PageId.DURING_WALK_K9_BL,
              },
              {
                target: PageId.URGE_LEASH_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DURING_WALK_K9_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: dogHasNoPullingLeash,
                target: PageId.LEASH_PULLING_K9_BL,
              },
              {
                target: PageId.URGE_LEASH_K9_BL,
              },
            ],
            NEXT_FLOW_STEP: [
              {
                cond: hasWalkProblems,
                target: PageId.URGE_NOT_ALONE_K9_BL,
              },
              {
                target: PageId.AGRESSIVE_BEHAVIOR_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.AGRESSIVE_BEHAVIOR_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: dogHasNoAgressiveBehavior,
                target: PageId.BEHAVIOR_ISSUES_K9_BL,
              },
              {
                target: PageId.TRIGGERS_K9_BL,
              },
            ],
            PREVIOUS_FLOW_STEP: [
              {
                cond: hasWalkProblems,
                target: PageId.URGE_NOT_ALONE_K9_BL,
              },
              {
                target: PageId.DURING_WALK_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.BEHAVIOR_ISSUES_K9_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: dogHasNoAgressiveBehavior,
                target: PageId.AGRESSIVE_BEHAVIOR_K9_BL,
              },
              {
                target: PageId.URGE_2_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_ACT_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: hasDogActDiferentlyAtHome,
                target: PageId.REWARDS_K9_BL,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.REWARDS_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: hasSelectedIgnoringCommands,
                target: PageId.COMMANDS_61_K9_BL,
              },
              {
                target: PageId.IGNORE_COMMANDS_K9_BL,
              },
            ],
            PREVIOUS_FLOW_STEP: [
              {
                cond: hasDogActDiferentlyAtHome,
                target: PageId.DOG_ACT_K9_BL,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.COMMANDS_61_K9_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: hasSelectedIgnoringCommands,
                target: PageId.REWARDS_K9_BL,
              },
              {
                target: PageId.IGNORE_COMMANDS_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_BREED_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isBreedIdSetupK9,
                target: PageId.DOG_BREED_ID_K9_BL,
              },
              {
                target: PageId.URGE_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_BREED_ID_K9_BL && {
            NEXT_FLOW_STEP: {
              target: PageId.URGE_K9_BL,
            },
            PREVIOUS_FLOW_STEP: {
              target: PageId.DOG_BREED_K9_BL,
            },
          }),
          ...(page.pageId === PageId.URGE_K9_BL && {
            PREVIOUS_FLOW_STEP: {
              target: PageId.DOG_BREED_K9_BL,
            },
          }),
          ...(page.pageId === PageId.NEWSLETTER_URGE_K9_BL && {
            NEXT_FLOW_STEP: PageId.SCRATCH_SCREEN_K9_BL,
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_K9_BL,
              },
              {
                target: PageId.CHAT_UPSELL_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_PROFILE_K9_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                target: PageId.URGE_3_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_K9_BL,
              },
              {
                target: PageId.UPSELL_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_K9_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return languageFromUrl === LOCALES.EN
                },
                target: PageId.UPSELL_FEEDING_GUIDE_K9_BL,
              },
              {
                target: PageId.ACCOUNT_K9_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_K9_BL && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_K9_BL,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_K9_BL && {
            NEXT_FLOW_STEP: PageId.ACCOUNT_K9_BL,
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_K9_BL]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_K9_BL,
    },
  },
  [PageId.DOWNLOAD_K9_BL]: {
    type: 'final',
  },
})
